/**
 * Lightbinder tier set: Vestments of the Dawn Serpent (crafting.md §5.4). `bonuses` target the
 * rotation-rework kit; the stack that Sunfall consumes is `lightbinder.dawnlight` (the lead
 * doc rotation-rework.md wins over crafting.md's "Gathering Dawn"). Greater Judgment's heal is a
 * heal nested in Judgment's Infusion conditional, so it is doubled with `scaleAmount deep`.
 */
import { ABILITY, CLASS } from '@keystone/content';
import type { EffectSpec } from '@keystone/sim';
import { TIER_SET_IDS, tierAdd, tierMult } from '../../forge.constants';
import { auraRef } from '../../transforms';
import type { TierSetDef } from '../../types';

const L = ABILITY.lightbinder;
const RW = {
  infusion: 'lightbinder.infusion_of_light',
  dawnlight: 'lightbinder.dawnlight',
  divinePurpose: 'lightbinder.divine_purpose',
  sunfall: 'lightbinder.sunfall',
  dawnbringer: 'lightbinder.dawnbringer',
} as const;

const infusionFeedsDawn: EffectSpec = {
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: RW.infusion },
  then: [{ type: 'applyAura', to: 'self', aura: auraRef(RW.dawnlight) }],
};

/** Sunfall: −2 s Dawnbringer per Dawnlight it consumes (ladder on minStacks, read before the consume). */
const dawnLadder: EffectSpec[] = [1, 2, 3, 4, 5, 6].map((k) => ({
  type: 'conditional',
  cond: { type: 'selfHasAura', auraId: RW.dawnlight, minStacks: k },
  then: [{ type: 'modifyCooldown', abilityId: RW.dawnbringer, deltaMs: -Math.round(tierAdd(2000)) }],
}));

export const LIGHTBINDER_SET: TierSetDef = {
  id: TIER_SET_IDS[CLASS.lightbinder]!,
  name: 'Vestments of the Dawn Serpent',
  classId: CLASS.lightbinder,
  pieceNames: { helm: 'Dawn Serpent Circlet', chest: 'Dawn Serpent Robes', legs: 'Dawn Serpent Leggings', gloves: 'Dawn Serpent Handwraps', boots: 'Dawn Serpent Slippers' },
  text: {
    2: { name: 'First Light', description: "Spending Infusion of Light grants 1 Dawnlight, Judgment deals 35% more damage, and Greater Judgment's heal is doubled." },
    4: { name: 'Serpent Sun', description: "Sunfall grants Divine Purpose and 2 Infusion of Light, and cuts Dawnbringer's cooldown by 2 s per Dawnlight consumed." },
  },
  bonuses: {
    2: [
      { op: 'addEffects', abilityId: L.flashHeal, position: 'start', effects: [infusionFeedsDawn] },
      { op: 'addEffects', abilityId: L.judgment, position: 'start', effects: [infusionFeedsDawn] },
      { op: 'scaleAmount', abilityId: L.judgment, effectType: 'heal', mult: tierMult(2), deep: true },
      { op: 'scaleAmount', abilityId: L.judgment, effectType: 'damage', mult: tierMult(1.35), deep: true },
    ],
    4: [
      {
        op: 'addEffects',
        abilityId: RW.sunfall,
        position: 'start',
        effects: [
          ...dawnLadder,
          { type: 'applyAura', to: 'self', aura: auraRef(RW.divinePurpose) },
          { type: 'applyAura', to: 'self', stacks: 2, aura: auraRef(RW.infusion) },
        ],
      },
    ],
  },
  interim: {
    text: {
      2: { name: 'First Light', description: 'Judgment also heals the most injured ally within 40 yd, and Holy Shock recharges 2 s faster.' },
      4: { name: 'Serpent Sun', description: 'Word of Light resets Holy Shock and grants 1 Holy Power.' },
    },
    bonuses: {
      2: [
        {
          op: 'addEffects',
          abilityId: L.judgment,
          effects: [
            {
              type: 'heal',
              to: 'area',
              area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'allies' },
              smart: 1,
              amount: { coef: tierAdd(2.0), variance: 0.05 },
            },
          ],
        },
        { op: 'modifyCooldown', abilityId: L.holyShock, deltaMs: -Math.round(tierAdd(2000)) },
      ],
      4: [
        {
          op: 'addEffects',
          abilityId: L.wordOfLight,
          effects: [
            { type: 'modifyCooldown', abilityId: L.holyShock, reset: true },
            { type: 'resource', to: 'self', resource: 'holy_power', amount: 1 },
          ],
        },
      ],
    },
  },
};
